import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose, withProps, withHandlers, withState } from 'recompose';
import mapDispatchPropsToActions from 'store/helpers/mapDispatchToActions';
import { searchMapSelector } from 'store/selectors/search';
import { getLocalPhoneNumberByZipCode } from 'store/pageMeta/actions';
import {
	mapRedoSearchAction,
	highlightFacility,
	clearHighlightedFacility,
} from 'store/search/actions';
import { Map } from 'components/search/SearchMap';
import { FacilityCard } from 'components/organisms/FacilityCard/FacilityCard';
import { isLarge } from 'utils/breakpoints';
import withViewportWidth from 'decorators/withViewportWidth';

import './embeddedSearchMap.scss';

export const enhance = compose(
	connect(
		searchMapSelector,
		{
			getLocalPhoneNumberByZipCode,
			mapRedoSearchAction,
			highlightFacility,
			clearHighlightedFacility,
		},
		mapDispatchPropsToActions
	),
	withProps(({ searchLocation }) => {
		if (!searchLocation) {
			return { mapLocation: null };
		}

		const hasLatitude =
			searchLocation.latitude !== undefined &&
			searchLocation.latitude !== null;
		const hasLongitude =
			searchLocation.longitude !== undefined &&
			searchLocation.longitude !== null;

		if (!hasLatitude || !hasLongitude) {
			return { mapLocation: null };
		}

		const lat = parseFloat(searchLocation.latitude);
		const lng = parseFloat(searchLocation.longitude);

		if (Number.isNaN(lat) || Number.isNaN(lng)) {
			return { mapLocation: null };
		}

		return {
			mapLocation: {
				lat,
				lng,
			},
			ariaMapLocation: `${searchLocation.city}, ${searchLocation.state}`,
		};
	}),
	withViewportWidth,
	withState('selectedMapFacilityId', 'setSelectedMapFacilityId', null),
	withHandlers({
		handleRedoSearch: (props) => (bounds) => {
			if (!bounds) {
				return;
			}

			const { lat: cLat, lng: cLng } = bounds.getCenter().toJSON();
			const { lat: neLat, lng: neLng } = bounds.getNorthEast().toJSON();
			const { lat: swLat, lng: swLng } = bounds.getSouthWest().toJSON();

			const swValid = !Number.isNaN(swLat) && !Number.isNaN(swLng);
			const neValid = !Number.isNaN(neLat) && !Number.isNaN(neLng);
			const centerValid = !Number.isNaN(cLat) && !Number.isNaN(cLng);

			if (centerValid && neValid && swValid) {
				if (typeof props.onMapSearchStart === 'function') {
					props.onMapSearchStart();
				}

				const boundary = `${swLng} ${swLat} ${neLng} ${neLat}`;
				if (typeof props.onSearch === 'function') {
					props.onSearch({
						location: `${cLat}, ${cLng}`,
						boundary,
						latitude: cLat,
						longitude: cLng,
					});
				}
				props.actions.mapRedoSearchAction(true);
			}
		},
		handleMarkerHover: (props) => (facilityId) => {
			if (facilityId !== null) {
				props.actions.highlightFacility(facilityId);
			}
		},
		handleMarkerLeave: (props) => () => {
			props.actions.clearHighlightedFacility();
		},
		handleShowMapFacilityCard:
			({ setSelectedMapFacilityId, viewportWidth }) =>
			(facilityId) => {
				// Only show card on mobile/tablet, not on large screens
				if (!isLarge(viewportWidth)) {
					setSelectedMapFacilityId(facilityId);
				}
			},
		handleCloseMapFacilityCard:
			({ setSelectedMapFacilityId }) =>
			() => {
				setSelectedMapFacilityId(null);
			},
	})
);

const FallbackFacilitiesList = ({ facilities }) => {
	return (
		<div className="embedded-search-map__empty">
			<p className="embedded-search-map__js-message">
				Please enable JavaScript for interactive map features.
			</p>
			{facilities?.map((facility) => (
				<div
					key={facility.id}
					className="embedded-search-map__facility"
				>
					<a href={facility.url?.facility || '#'}>
						<h4>{facility.name}</h4>
						<p>{[facility.location?.streetAddress, facility.location?.city, `${facility.location?.state || ''} ${facility.location?.zip || ''}`.trim()].filter(Boolean).join(', ')}</p>
						{facility.distance && (
							<p>{facility.distance} miles away</p>
						)}
					</a>
				</div>
			))}
		</div>
	);
};

// Utilized SearchMap component as a reference for this component
export function EmbeddedSearchMap({
	mapLocation,
	facilities,
	facilitiesPerPage,
	totalFacilities,
	page,
	preselectedFacilityId,
	zoom = 15,
	actions,
	googleMapsKey,
	localPhone,
	highlightedFacility,
	searchOnMove = true,
	onMapReady,
	handleRedoSearch,
	handleMarkerHover,
	handleMarkerLeave,
	onScrollToFacility,
	searchParams,
	searchType,
	baseUrl,
	selectedMapFacilityId,
	handleShowMapFacilityCard,
	handleCloseMapFacilityCard,
	viewportWidth,
	ariaMapLocation,
}) {
	const selectedFacility = selectedMapFacilityId
		? facilities?.find((facility) => facility.id === selectedMapFacilityId)
		: null;

	const showCard = !isLarge(viewportWidth) && selectedFacility;

	return (
		<div
			className="embedded-search-map"
			{...(mapLocation && {
				'aria-label': `Interactive Map of Storage Facilities in ${ariaMapLocation}`,
			})}
			role="application"
		>
			{mapLocation && (
				<Map
					googleMapsKey={googleMapsKey}
					facilities={facilities}
					highlightedFacility={highlightedFacility}
					onReady={onMapReady}
					searchLocation={mapLocation}
					initialZoom={zoom}
					onRedoSearch={handleRedoSearch}
					totalFacilities={totalFacilities}
					facilitiesPerPage={facilitiesPerPage}
					page={page}
					phone={localPhone}
					getLocalPhoneNumberByZipCode={
						actions.getLocalPhoneNumberByZipCode
					}
					searchOnMove={searchOnMove}
					preselectedFacilityId={preselectedFacilityId}
					isNewSearchPage={true}
					onMarkerHover={handleMarkerHover}
					onMarkerLeave={handleMarkerLeave}
					onScrollToFacility={onScrollToFacility}
					searchParams={searchParams}
					searchType={searchType}
					baseUrl={baseUrl}
					onShowMapFacilityCard={handleShowMapFacilityCard}
				/>
			)}

			{/* Static facility card at bottom of map */}
			{!!showCard && !!selectedFacility && (
				<>
					<div
						className="map-card-backdrop"
						onClick={handleCloseMapFacilityCard}
						role="button"
						tabIndex={0}
						onKeyDown={(e) => {
							if (e.key === 'Escape') {
								handleCloseMapFacilityCard();
							}
						}}
						aria-label="Close facility card"
					/>
					<div className="map-facility-card-static">
						<FacilityCard
							facility={selectedFacility}
							baseUrl={baseUrl || ''}
							searchParams={searchParams}
							storageType={searchType}
							lazyLoad={false}
							hasOMI={selectedFacility.hasOnlineMoveins}
							withSchema={false}
						/>
					</div>
				</>
			)}

			{/* if JavaScript is disabled (edge case) */}
			<noscript>
				<FallbackFacilitiesList facilities={facilities} />
			</noscript>
		</div>
	);
}

EmbeddedSearchMap.propTypes = {
	searchLocation: PropTypes.shape({
		latitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		longitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	}),
	mapLocation: PropTypes.shape({
		lat: PropTypes.number,
		lng: PropTypes.number,
	}),
	ariaMapLocation: PropTypes.string,
	facilities: PropTypes.arrayOf(PropTypes.object),
	facilitiesPerPage: PropTypes.number,
	totalFacilities: PropTypes.number,
	page: PropTypes.number,
	preselectedFacilityId: PropTypes.number,
	zoom: PropTypes.number,
	onSearch: PropTypes.func,
	actions: PropTypes.object,
	googleMapsKey: PropTypes.string,
	localPhone: PropTypes.object,
	highlightedFacility: PropTypes.number,
	searchOnMove: PropTypes.bool,
	onMapReady: PropTypes.func,
	onMapSearchStart: PropTypes.func,
	handleRedoSearch: PropTypes.func,
	handleMarkerHover: PropTypes.func,
	handleMarkerLeave: PropTypes.func,
	onScrollToFacility: PropTypes.func,
	searchParams: PropTypes.object,
	searchType: PropTypes.string,
	baseUrl: PropTypes.string,
	selectedMapFacilityId: PropTypes.number,
	handleShowMapFacilityCard: PropTypes.func,
	handleCloseMapFacilityCard: PropTypes.func,
	viewportWidth: PropTypes.number,
};

FallbackFacilitiesList.propTypes = {
	facilities: PropTypes.arrayOf(PropTypes.object),
};

export default enhance(EmbeddedSearchMap);
